import axios from "axios";
import { getToken } from "../services/AuthToken";
import { enqueueSnackbar } from "notistack";
import { setApiErrorState } from "./apiErrorState";
import { getApiBaseUrl } from "./serverUrl";

const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
});

const showApiErrorPage = ({ status, data }) => {
  setApiErrorState({
    status: Number(status || 500),
    message: data?.message || "Something went wrong while processing your request.",
    path: data?.path || "",
    timestamp: data?.timestamp || "",
  });
};

// Request interceptor → attach token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor → central error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;
    const suppressGlobalError = Boolean(error?.config?.suppressGlobalError);

    if (response && !suppressGlobalError) {
      const { status, data } = response;

      // 🔒 Unauthorized or JWT Expired
      if (
        status === 401 ||
        (status === 500 &&
          (data?.message?.toLowerCase().includes("jwt is expired") ||
            data?.message?.toLowerCase().includes("user not found with username")))
      ) {
        showApiErrorPage({
          status: 401,
          data: {
            ...data,
            message: data?.message || "Your session has expired. Please log in again.",
          },
        });
      }

      // 🚫 Forbidden
      else if (status === 403) {
        showApiErrorPage({ status, data });
      }

      // ❌ Not Found
      else if (status === 404) {
        showApiErrorPage({ status, data });
      }

      // 💥 Server Error
      else if (status >= 500) {
        showApiErrorPage({ status, data });
      }
    } else if (!suppressGlobalError) {
      // 🌐 Network Error
      enqueueSnackbar("Network error. Please check your connection.", { variant: "error" });
    }

    return Promise.reject(error);
  }
);

// Export API client wrapper
const apiClient = {
  get: (url, config = {}) => axiosInstance.get(url, config),
  post: (url, data, config = {}) => axiosInstance.post(url, data, config),
  put: (url, data, config = {}) => axiosInstance.put(url, data, config),
  patch: (url, data, config = {}) => axiosInstance.patch(url, data, config),
  delete: (url, config = {}) => axiosInstance.delete(url, config),
};

export default apiClient;
